import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const STATUS_META = {
  pending:   { label: 'Pending',   color: 'bg-yellow-100 text-yellow-800', icon: 'fa-hourglass-half' },
  delivered: { label: 'Delivered', color: 'bg-blue-100 text-blue-800',     icon: 'fa-reply' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800',   icon: 'fa-check-circle' },
  closed:    { label: 'Closed',    color: 'bg-gray-100 text-gray-700',     icon: 'fa-lock' },
};

const PRIORITY_META = {
  low:    'bg-gray-100 text-gray-600',
  normal: 'bg-blue-100 text-blue-700',
  high:   'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

const CATEGORY_META = {
  general:    'General',
  complaint:  'Complaint',
  request:    'Request',
  suggestion: 'Suggestion',
};


export default function Notes() {
  const { user } = useAuth();
  const role = user?.role;

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);

  const canCreate = role === 'parent';

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/notes/');
      setNotes(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return notes;
    return notes.filter(n => n.status === statusFilter);
  }, [notes, statusFilter]);

  const counts = useMemo(() => ({
    all: notes.length,
    pending: notes.filter(n => n.status === 'pending').length,
    delivered: notes.filter(n => n.status === 'delivered').length,
    completed: notes.filter(n => n.status === 'completed').length,
    closed: notes.filter(n => n.status === 'closed').length,
  }), [notes]);

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <i className="fas fa-envelope text-blue-500"></i>
            Notes & Complaints
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {role === 'parent'
              ? 'Send a note to your child\'s class teacher.'
              : role === 'teacher'
              ? 'Notes from parents of students in your class.'
              : role === 'principal'
              ? 'All parent-teacher notes in your campus.'
              : 'All parent-teacher notes across campuses.'}
          </p>
        </div>

        <div className="flex gap-2 items-center">
          <button onClick={load} disabled={loading}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
            <i className="fas fa-sync-alt"></i>
          </button>
          {canCreate && (
            <button onClick={() => setComposerOpen(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm">
              <i className="fas fa-plus mr-1"></i> New Note
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { key: 'all',       label: 'All',       count: counts.all },
          { key: 'pending',   label: 'Pending',   count: counts.pending },
          { key: 'delivered', label: 'Delivered', count: counts.delivered },
          { key: 'completed', label: 'Completed', count: counts.completed },
          { key: 'closed',    label: 'Closed',    count: counts.closed },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 py-1.5 text-xs rounded border transition font-semibold ${
              statusFilter === f.key
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
            }`}
          >
            {f.label}
            <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
              statusFilter === f.key ? 'bg-white/20' : 'bg-gray-100 text-gray-600'
            }`}>
              {f.count}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bg-white p-8 rounded shadow text-center text-gray-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white p-12 rounded shadow text-center text-gray-400">
          <i className="fas fa-inbox text-4xl mb-3"></i>
          <p>No notes found{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}.</p>
          {canCreate && <p className="text-xs mt-2">Click "New Note" to send one.</p>}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(n => {
            const sMeta = STATUS_META[n.status] || STATUS_META.pending;
            return (
              <div
                key={n.id}
                className="bg-white p-4 rounded shadow hover:shadow-md transition cursor-pointer border-l-4 border-blue-400"
                onClick={() => setSelected(n)}
              >
                <div className="flex justify-between items-start gap-3 flex-wrap mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${sMeta.color}`}>
                      <i className={`fas ${sMeta.icon} mr-1`}></i> {sMeta.label}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${PRIORITY_META[n.priority]}`}>
                      {n.priority}
                    </span>
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                      {CATEGORY_META[n.category] || n.category}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">
                    {n.created_at_display || '—'}
                  </span>
                </div>

                <h3 className="font-bold text-gray-800 mb-1">{n.subject}</h3>
                <p className="text-sm text-gray-600 line-clamp-2">{n.message}</p>

                <div className="flex justify-between items-center mt-3 pt-2 border-t text-xs text-gray-500">
                  <div className="flex flex-wrap gap-3">
                    <span><i className="fas fa-user mr-1"></i> From: <strong>{n.parent_name}</strong></span>
                    <span><i className="fas fa-user-graduate mr-1"></i> {n.student_name}</span>
                    <span><i className="fas fa-chalkboard mr-1"></i> {n.class_name || '—'}</span>
                    {n.teacher_name && (
                      <span><i className="fas fa-chalkboard-teacher mr-1"></i> To: <strong>{n.teacher_name}</strong></span>
                    )}
                  </div>
                  {n.teacher_reply && (
                    <span className="text-blue-600">
                      <i className="fas fa-comment-dots mr-1"></i> 1 reply
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {composerOpen && (
        <NewNoteModal
          onClose={() => setComposerOpen(false)}
          onCreated={() => { setComposerOpen(false); load(); }}
        />
      )}

      {selected && (
        <NoteDetailModal
          note={selected}
          role={role}
          userId={user?.id}
          onClose={() => setSelected(null)}
          onUpdated={(updated) => {
            setNotes(prev => prev.map(x => x.id === updated.id ? updated : x));
            setSelected(updated);
          }}
          onDeleted={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// New Note composer — parents only
// ═══════════════════════════════════════════════════════════════════
function NewNoteModal({ onClose, onCreated }) {
  const [children, setChildren] = useState([]);
  const [studentId, setStudentId] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('normal');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/parent-students/my-children')
      .then(r => {
        setChildren(r.data || []);
        if (r.data?.length) setStudentId(r.data[0].student_id);
      })
      .catch(err => setError(err.response?.data?.detail || err.message));
  }, []);

  const handleSubmit = async () => {
    setError('');
    if (!studentId) { setError('Please select a child'); return; }
    if (!subject.trim() || !message.trim()) { setError('Subject and message are required'); return; }

    setLoading(true);
    try {
      await api.post('/notes/', {
        student_id: parseInt(studentId),
        subject: subject.trim(),
        message: message.trim(),
        category,
        priority,
      });
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-bold">Send a Note to Teacher</h3>
          <button onClick={onClose} className="text-2xl text-gray-400 hover:text-gray-700">×</button>
        </div>

        <div className="p-4 space-y-3">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded border border-red-200 text-sm">
              {error}
            </div>
          )}

          {children.length === 0 ? (
            <p className="text-gray-500 text-sm">No children linked to your account.</p>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Child *</label>
                <select className="w-full p-2 border rounded" value={studentId}
                        onChange={e => setStudentId(e.target.value)}>
                  {children.map(c => (
                    <option key={c.student_id} value={c.student_id}>
                      {c.name} — {c.class_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Subject *</label>
                <input className="w-full p-2 border rounded" value={subject}
                       onChange={e => setSubject(e.target.value)}
                       placeholder="e.g., Homework concern"
                       maxLength={150} autoFocus />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Message *</label>
                <textarea className="w-full p-2 border rounded" rows="5" value={message}
                          onChange={e => setMessage(e.target.value)}
                          placeholder="Explain the situation…" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                  <select className="w-full p-2 border rounded" value={category}
                          onChange={e => setCategory(e.target.value)}>
                    <option value="general">General</option>
                    <option value="complaint">Complaint</option>
                    <option value="request">Request</option>
                    <option value="suggestion">Suggestion</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                  <select className="w-full p-2 border rounded" value={priority}
                          onChange={e => setPriority(e.target.value)}>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <p className="text-xs bg-blue-50 text-blue-700 p-2 rounded">
                <i className="fas fa-info-circle mr-1"></i>
                This note goes directly to your child's class teacher.
                The principal can view it for oversight.
              </p>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <button onClick={onClose} disabled={loading}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading || children.length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
            {loading ? 'Sending…' : 'Send Note'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Detail modal — view + reply + acknowledge
// ═══════════════════════════════════════════════════════════════════
function NoteDetailModal({ note, role, userId, onClose, onUpdated, onDeleted }) {
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isTeacher = role === 'teacher' && note.teacher_id === userId;
  const isParent = role === 'parent' && note.parent_id === userId;
  const isManager = role === 'admin' || role === 'superintendent' ||
                    (role === 'principal');

  const canReply = isTeacher && (note.status === 'pending');
  const canAcknowledge = isParent && note.status === 'delivered';
  const canClose = isManager && !['completed', 'closed'].includes(note.status);
  const canDelete = role === 'admin';

  const sMeta = STATUS_META[note.status] || STATUS_META.pending;

  const submitReply = async () => {
    setError('');
    if (!reply.trim()) { setError('Reply cannot be empty'); return; }
    setBusy(true);
    try {
      const res = await api.post(`/notes/${note.id}/reply`, { teacher_reply: reply.trim() });
      onUpdated(res.data);
      setReply('');
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const acknowledge = async () => {
    if (!window.confirm('Confirm this note is resolved?')) return;
    setBusy(true);
    try {
      const res = await api.post(`/notes/${note.id}/acknowledge`);
      onUpdated(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    if (!window.confirm('Close this note?')) return;
    setBusy(true);
    try {
      const res = await api.put(`/notes/${note.id}/close`);
      onUpdated(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Permanently delete this note?')) return;
    setBusy(true);
    try {
      await api.delete(`/notes/${note.id}`);
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start p-4 border-b bg-gray-50">
          <div>
            <h3 className="text-lg font-bold text-gray-800">{note.subject}</h3>
            <div className="flex flex-wrap gap-2 mt-1">
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${sMeta.color}`}>
                <i className={`fas ${sMeta.icon} mr-1`}></i> {sMeta.label}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${PRIORITY_META[note.priority]}`}>
                {note.priority}
              </span>
              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                {CATEGORY_META[note.category] || note.category}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-2xl text-gray-400 hover:text-gray-700">×</button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 p-2 rounded border border-red-200 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <div className="bg-gray-50 p-2 rounded">
              <p className="text-gray-500 mb-0.5">From</p>
              <p className="font-semibold">{note.parent_name}</p>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <p className="text-gray-500 mb-0.5">Student</p>
              <p className="font-semibold">{note.student_name} · {note.class_name || '—'}</p>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <p className="text-gray-500 mb-0.5">Teacher</p>
              <p className="font-semibold">{note.teacher_name}</p>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
              Parent's Message · {note.created_at_display || '—'}
            </p>
            <p className="text-sm text-gray-800 whitespace-pre-line bg-gray-50 p-3 rounded">
              {note.message}
            </p>
          </div>

          {note.teacher_reply ? (
            <div>
              <p className="text-xs font-semibold text-blue-600 uppercase mb-1">
                Teacher's Reply
                {note.teacher_replied_at_display ? ` · ${note.teacher_replied_at_display}` : ''}
              </p>
              <p className="text-sm text-gray-800 whitespace-pre-line bg-blue-50 border border-blue-200 p-3 rounded">
                {note.teacher_reply}
              </p>
            </div>
          ) : canReply ? (
            <div>
              <p className="text-xs font-semibold text-blue-600 uppercase mb-1">Your Reply</p>
              <textarea
                className="w-full p-2 border rounded text-sm"
                rows="4"
                value={reply}
                onChange={e => setReply(e.target.value)}
                placeholder="Write your reply…"
                autoFocus
              />
              <button
                onClick={submitReply}
                disabled={busy}
                className="mt-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm disabled:opacity-50"
              >
                <i className="fas fa-paper-plane mr-1"></i> Send Reply
              </button>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded text-sm text-yellow-800">
              <i className="fas fa-hourglass-half mr-1"></i>
              Waiting for the teacher to reply.
            </div>
          )}

          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Timeline</p>
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-gray-600">Created</span>
                <span className="ml-auto text-gray-400">
                  {note.created_at_display || '—'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${note.teacher_replied_at ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                <span className={note.teacher_replied_at ? 'text-gray-600' : 'text-gray-400'}>
                  Teacher replied
                </span>
                <span className="ml-auto text-gray-400">
                  {note.teacher_replied_at_display || '—'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${note.parent_acknowledged_at ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                <span className={note.parent_acknowledged_at ? 'text-gray-600' : 'text-gray-400'}>
                  Parent acknowledged
                </span>
                <span className="ml-auto text-gray-400">
                  {note.parent_acknowledged_at_display || '—'}
                </span>
              </div>
            </div>
          </div>

          {canAcknowledge && (
            <button
              onClick={acknowledge}
              disabled={busy}
              className="w-full bg-green-600 text-white py-2.5 rounded hover:bg-green-700 font-medium disabled:opacity-50"
            >
              <i className="fas fa-check-circle mr-1"></i>
              Acknowledge — Mark as Resolved
            </button>
          )}

          {note.status === 'completed' && (
            <div className="bg-green-50 border border-green-200 p-3 rounded text-sm text-green-800 text-center">
              <i className="fas fa-check-circle mr-1"></i>
              Parent confirmed this note is resolved.
            </div>
          )}

          {note.status === 'closed' && (
            <div className="bg-gray-100 border border-gray-200 p-3 rounded text-sm text-gray-700 text-center">
              <i className="fas fa-lock mr-1"></i>
              This note was closed by management.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
            Close
          </button>
          {canClose && (
            <button onClick={close} disabled={busy}
                    className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50">
              <i className="fas fa-lock mr-1"></i> Force Close
            </button>
          )}
          {canDelete && (
            <button onClick={remove} disabled={busy}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
              <i className="fas fa-trash mr-1"></i> Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}